import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Instructors.css';

const InstructorSlider = () => {
  const { t } = useTranslation();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const sliderTrackRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const response = await axios.get('instructors');
        
        // Duplicate items for seamless looping
        const duplicatedItems = [...response.data, ...response.data, ...response.data, ...response.data, ...response.data, ...response.data, ...response.data, ...response.data]; // 8x for smoother looping
        
        setInstructors(duplicatedItems);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchInstructors();
  }, []);

  // Auto-scroll animation
  useEffect(() => {
    if (instructors.length === 0 || !sliderTrackRef.current) return;
    
    let animationFrameId;
    let lastTimestamp;
    
    const animate = (timestamp) => {
      if (!isPaused) {
        if (lastTimestamp) {
          const diff = timestamp - lastTimestamp;
          // Adjust the division factor to control speed
          const moveAmount = diff / 30;
          
          setScrollPosition(prevPosition => {
            let newPosition = prevPosition + moveAmount;
            
            // Reset position for seamless looping
            const itemWidth = getItemWidth();
            const originalInstructorsLength = instructors.length / 3;
            const totalFirstSetWidth = originalInstructorsLength * itemWidth;
            
            if (newPosition >= totalFirstSetWidth) {
              newPosition = 0;
            }
            
            return newPosition;
          });
        }
        lastTimestamp = timestamp;
      } else {
        lastTimestamp = 0;
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isPaused, instructors]);

  const getItemsPerView = () => {
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 3;
  };

  const getItemWidth = () => {
    if (!containerRef.current) return 300; // Default fallback
    return containerRef.current.offsetWidth / getItemsPerView();
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  const handleNext = () => {
    const scrollAmount = getItemWidth();
    setScrollPosition(prev => {
      let newPosition = prev + scrollAmount;
      
      // Handle loop boundaries
      const originalInstructorsLength = instructors.length / 3;
      const totalWidth = originalInstructorsLength * getItemWidth();
      
      if (newPosition >= totalWidth) {
        newPosition = 0;
      }
      
      return newPosition;
    });
  };

  const handlePrev = () => {
    const scrollAmount = getItemWidth();
    setScrollPosition(prev => {
      let newPosition = prev - scrollAmount;
      
      // Handle loop boundaries
      const originalInstructorsLength = instructors.length / 3;
      const totalWidth = originalInstructorsLength * getItemWidth();
      
      if (newPosition < 0) {
        newPosition = totalWidth - (scrollAmount / 2);
      }
      
      return newPosition;
    });
  };

  if (loading) {
    return <div className="loading">{t('loading_instructors')}</div>;
  }

  if (error) {
    return <div className="error">{t('error')} {error}</div>;
  }

  if (!instructors.length) {
    return <div className="no-instructors">{t('no_instructors')}</div>;
  }

  return (
    <section className="instructors-section" id="instructors">
      <div className="section-header">
        <h2>{t('our_instructors')}</h2>
        <p>{t('meet_team')}</p>
      </div>
      
      <div className="slider-container" ref={containerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <button className="slider-arrow prev-arrow" onClick={handlePrev}>
          &#8249;
        </button>
        
        <div 
          className="slider-track"
          ref={sliderTrackRef}
          style={{
            transform: `translateX(-${scrollPosition}px)`,
            transition: isPaused ? 'none' : 'transform 0.1s linear'
          }}
        >
          {instructors.map((instructor, index) => (
            <div className="slider-item" key={`${instructor.id}-${index}`}>
              <div className="instructor-card">
                <div className="instructor-image">
                  <img src={instructor.image_url || '/images/default-instructor.jpg'} alt={instructor.name} />
                </div>
                <div className="instructor-details">
                  <h3 className="instructor-name">{instructor.name}</h3>
                  <p className="instructor-specialty">{instructor.specialty || t('yoga_instructor')}</p>
                  <div className="action-buttons">
                    <a href={`/instructors/${instructor.id}`} className="profile-button">{t('view_profile')}</a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <button className="slider-arrow next-arrow" onClick={handleNext}>
          &#8250;
        </button>
      </div>
    </section>
  );
};

export default InstructorSlider; 